import {
  useContext,
  useEffect,
  useReducer,
  useState,
  type PropsWithChildren
} from 'react';
import AppContext from '@/context/Context';
import {
  configReducer,
  settings,
  type AppContextValue,
  type ConfigState,
  type Theme
} from '@/config';
import { getColor, getItemFromStore } from '@/helpers/utils';
import useToggleStyle from '@/hooks/useToggleStyle';

import { Chart as ChartJS, registerables } from 'chart.js';
ChartJS.register(...registerables);

const PERSISTED_CONFIG_KEYS: (keyof ConfigState)[] = [
  'isFluid',
  'themeVariant',
  'isRTL',
  'isDark',
  'theme',
  'navbarPosition',
  'isNavbarVerticalCollapsed',
  'navbarStyle'
];

const Main = ({ children }: PropsWithChildren) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const configState: ConfigState = {
    isFluid: getItemFromStore('isFluid', settings.isFluid),
    isRTL: getItemFromStore('isRTL', settings.isRTL),
    isDark: getItemFromStore('isDark', settings.isDark),
    theme: getItemFromStore('theme', settings.theme),
    themeVariant: getItemFromStore('themeVariant', settings.themeVariant),
    navbarPosition: getItemFromStore('navbarPosition', settings.navbarPosition),
    disabledNavbarPosition: [],
    isNavbarVerticalCollapsed: getItemFromStore(
      'isNavbarVerticalCollapsed',
      settings.isNavbarVerticalCollapsed
    ),
    navbarStyle: getItemFromStore('navbarStyle', settings.navbarStyle),
    currency: settings.currency,
    showBurgerMenu: settings.showBurgerMenu,
    showSettingPanel: false,
    navbarCollapsed: false,
    openAuthModal: false
  };

  const [config, configDispatch] = useReducer(configReducer, configState);

  const setConfig = <K extends keyof ConfigState>(
    key: K,
    value: ConfigState[K]
  ) => {
    configDispatch({
      type: 'SET_CONFIG',
      payload: {
        key,
        value,
        setInStore: PERSISTED_CONFIG_KEYS.includes(key)
      }
    });
  };

  const { isLoaded } = useToggleStyle(
    config.isRTL,
    config.isDark,
    config.themeVariant
  );

  useEffect(() => {
    const isDark = config.theme === 'dark';
    setConfig('isDark', isDark);
  }, [config.theme]);

  const changeTheme = (theme: Theme) => {
    const isDark =
      theme === 'auto' ? config.theme === 'dark' : theme === 'dark';
    document.documentElement.setAttribute(
      'data-bs-theme',
      isDark ? 'dark' : 'light'
    );
    setConfig('theme', theme);
    setConfig('isDark', isDark);
  };

  const changeThemeVariant = (theme: string) => {
    document.documentElement.setAttribute('data-theme-variant', theme);
    setConfig('themeVariant', theme);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(true);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const goftino_p = localStorage.getItem('goftino_p');

  if (!isLoaded) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          backgroundColor: config.isDark ? getColor('dark') : getColor('light')
        }}
      />
    );
  }

  return (
    <AppContext.Provider
      value={
        {
          config,
          setConfig,
          configDispatch,
          changeTheme,
          changeThemeVariant,
          isFullscreen
        } as unknown as typeof settings
      }
    >
      <style>
        {`#goftino_w{
        ${goftino_p ? goftino_p : 'left'}:20px !important;
        }
        `}
      </style>
      {children}
    </AppContext.Provider>
  );
};

export type { AppContextValue } from '@/config';

export const useAppContext = (): AppContextValue =>
  useContext(AppContext) as unknown as AppContextValue;

export default Main;
